// Single source of truth for per-route SEO metadata.
//
// Deliberately CommonJS: this table is read from two places that have no build
// step in common -- the React app (src/hooks/usePageMeta.js, via webpack) and
// the Node postbuild prerenderer (scripts/prerender-seo.js). CommonJS is the
// only module format both can consume as-is, and keeping one table means a page
// can never end up with a prerendered <title> that disagrees with the one the
// app swaps in on mount.

const SITE_ORIGIN = 'https://www.vasi.ai';

// The only hostnames allowed to appear in search results. QA (vasi-qa.web.app,
// vasi-qa.firebaseapp.com), Firebase preview channels and localhost are all
// serving the same bundle, and QA is publicly reachable, so "is this indexable?"
// has to be answered from the host rather than assumed from the build.
//
// Checked at runtime as well as at build time: the prerenderer stamps the right
// tags in for each environment, and this list stops a prod-flagged build from
// claiming to be indexable if it is ever served from somewhere else.
const PRODUCTION_HOSTS = ['www.vasi.ai', 'vasi.ai'];

function isProductionHost(hostname) {
  return PRODUCTION_HOSTS.includes(hostname);
}

// Versioned filename on purpose. Google, Facebook, LinkedIn, WhatsApp and Slack
// all cache preview images keyed by URL, so replacing the bytes behind a stable
// path leaves every one of them serving the old picture for weeks. Swapping the
// artwork means bumping the suffix here and renaming the file in public/ -- a
// new URL is the only thing that reliably busts those caches.
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-default-v2.webp`;
const DEFAULT_OG_IMAGE_ALT = 'VaSi app preview with a live Experience and Smart Tasks';

// Used for any route without its own entry below -- i.e. everything served by
// the SPA fallback. Intentionally generic: these routes are not indexed, so this
// text only ever shows in a link preview, never in a search result.
const DEFAULT_META = {
  title: 'LEARN. APPLY. CREATE.',
  description:
    'VaSi is a platform to explore, share, and monetize expert knowledge.',
  image: DEFAULT_OG_IMAGE,
};

// The pages we want Google to surface. Each entry is prerendered to its own HTML
// file at build time and listed in sitemap.xml. Adding a route here is all it
// takes to make it indexable; nothing else needs editing.
//
// `lastmod` is the content's own last-meaningful-change date, not the build date
// -- stamping today's date on every build teaches Google that lastmod is noise.
const ROUTE_META = {
  '/': {
    title: 'Live Online Workshops & Knowledge Guides | VaSi',
    description:
      'Learn from practitioners through live online sessions and actionable guides on VaSi. SmartTasks help you apply what you learned.',
    lastmod: '2026-08-31',
    priority: '1.0',
    changefreq: 'weekly',
  },
  '/creators': {
    title: 'Host Paid Online Workshops | VaSi for Creators',
    description:
      'Host paid live sessions on VaSi with a landing page, Zoom room, payments, community and recordings in one app. Request a creator setup call.',
    lastmod: '2026-08-31',
    priority: '0.9',
    changefreq: 'weekly',
  },
  '/about-us': {
    title: 'About VaSi - Our Mission & Vision',
    description:
      "Learn about VaSi's mission and vision: one platform, three formats, and two kinds of impact for the people who share knowledge and the people who need it.",
    lastmod: '2026-08-31',
    priority: '0.7',
    changefreq: 'monthly',
  },
  '/faq': {
    title: 'Frequently Asked Questions | VaSi',
    description:
      'Answers to common questions about VaSi - how Experiences and Dots work, how creators get paid, payments, refunds, and account help.',
    lastmod: '2026-08-31',
    priority: '0.7',
    changefreq: 'monthly',
  },
  '/privacy-policy-usa': {
    title: 'Privacy Policy (USA) | VaSi',
    description:
      'How VaSi collects, uses, stores, and protects personal information for users in the United States, and the privacy rights available to you.',
    lastmod: '2026-08-31',
    priority: '0.3',
    changefreq: 'yearly',
  },
};

// Routes that render the same content as the homepage in production are mapped
// to its canonical URL. The production homepage enables ConsumerLandingPageV2,
// which is also rendered by /earn and /download. None of these aliases redirect;
// their current navigation and page behaviour stay intact.
const CANONICAL_ALIASES = {
  '/discover': '/',
  '/download': '/',
  '/earn': '/',
};

// Routes that must never reach a search result: private/auth-gated areas,
// in-app WebView pages, transactional post-checkout screens, link-shortener
// hops, and user-generated Experience pages.
//
// These stay *crawlable* on purpose -- robots.txt must not block them, because a
// blocked URL is one Google can never fetch and therefore never sees the noindex
// on. Blocking instead of noindexing is what leaves bare URLs sitting in the
// index with no title.
const NOINDEX_PATTERNS = [
  /^\/dashboard(\/|$)/,
  /^\/dots-details(\/|$)/,
  /^\/login$/,
  /^\/details$/,
  /^\/dots$/,
  /^\/deeplink(\/|$)/,
  /^\/dl(\/|$)/,
  /^\/share(\/|$)/,
  /^\/ex(\/|$)/,
  /^\/debug(\/|$)/,
  /^\/welcome-new-user$/,
  /^\/registered-(success|cancel)$/,
  /^\/registration-(confirmation|success|seats-full|payment-failed)$/,
  /\/registration-(confirmation|success|seats-full|payment-failed)(\/|$)/,
  // WebView-only funnels -- these render inside the mobile app, not the website.
  /^\/become-a-host$/,
  /^\/become-a-creator$/,
  /^\/monetize-your-profile$/,
];

// Trailing slashes are stripped so `/faq/` and `/faq` resolve to one entry --
// they are the same page and must not compete with each other in the index.
function normalizePath(pathname) {
  if (!pathname) return '/';
  const withoutQuery = pathname.split('?')[0].split('#')[0];
  return withoutQuery !== '/' ? withoutQuery.replace(/\/+$/, '') || '/' : '/';
}

function isNoIndex(pathname) {
  const path = normalizePath(pathname);
  if (ROUTE_META[path]) return false;
  return NOINDEX_PATTERNS.some((pattern) => pattern.test(path));
}

// An alias resolves to its twin's URL; an indexed route to itself; anything else
// gets no canonical at all, so we never assert that an arbitrary SPA route is
// the preferred version of something.
function resolveCanonical(path) {
  const alias = CANONICAL_ALIASES[path];
  if (alias) return `${SITE_ORIGIN}${alias === '/' ? '/' : alias}`;
  if (ROUTE_META[path]) return `${SITE_ORIGIN}${path === '/' ? '/' : path}`;
  return null;
}

// Returns everything needed to populate <head> for a path.
//
// An alias inherits its twin's copy as well as its canonical: /discover shows the
// homepage's markup, so it should also carry the homepage's title rather than the
// generic fallback. `canonical` is always absolute -- a relative one is ignored.
//
// `hasOwnMeta` tells the caller whether this table actually knows the page. When
// false, the runtime hook must leave title/description alone, because the route
// component owns them (Experience pages and the legal pages set their own) and
// overwriting would replace a specific title with a generic one.
function getMetaForPath(pathname) {
  const path = normalizePath(pathname);
  const aliasTarget = CANONICAL_ALIASES[path];
  const entry = ROUTE_META[path] || (aliasTarget ? ROUTE_META[aliasTarget] : null);

  return {
    path,
    title: (entry && entry.title) || DEFAULT_META.title,
    description: (entry && entry.description) || DEFAULT_META.description,
    image: (entry && entry.image) || DEFAULT_META.image,
    canonical: resolveCanonical(path),
    noindex: isNoIndex(path),
    hasOwnMeta: Boolean(entry),
  };
}

module.exports = {
  SITE_ORIGIN,
  PRODUCTION_HOSTS,
  isProductionHost,
  DEFAULT_META,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_ALT,
  ROUTE_META,
  CANONICAL_ALIASES,
  NOINDEX_PATTERNS,
  normalizePath,
  isNoIndex,
  resolveCanonical,
  getMetaForPath,
};
